// Buffer polyfill — @react-pdf/renderer's fetchImage decodes
// arraybuffer responses via `Buffer.from(...)`, which is a Node.js
// global. Browsers don't ship one, so without this polyfill PDF
// rendering throws "Buffer is not defined" the moment a real <Image>
// asset (logo, property photo) needs to be fetched. Imported from the
// already-present `buffer` package; assignment to globalThis must
// happen before any react-pdf code runs.
import { Buffer } from 'buffer';
if (typeof globalThis.Buffer === 'undefined') {
  globalThis.Buffer = Buffer;
}
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { ToastProvider } from './contexts/ToastContext';
import { ColorSchemeProvider } from './components/ColorSchemeProvider';
import { AuthProvider } from './contexts/AuthContext';
import { CACHE_TIERS, getStaticEntities, getQueryDefaults } from '@aiga/app-config/cache';
import App from './App';
import { initSentry } from './lib/sentry';
import { ErrorBoundary } from './utils/error-handler';
import { installGoogleMapsAuthFailureHandler } from './lib/google-maps-auth';
import { propertyDealsKeys } from './api/extensions/properties/usePropertyDeals';
import { dashboardKeys } from './api/extensions/dashboard/dashboardSummary';
import { propertyFileKeys } from './hooks/usePropertyFiles';
import { compsRowsKeys } from './api/extensions/comps/compsRows';
import { reviewQueuesKeys } from './api/extensions/reviews/reviewQueues';
import { compReportEntriesKeys } from './api/extensions/comps/compReportEntries';
import './index.css';
import './styles/theme-styles.css';
import './styles/saveable-highlight.css';

// Must run before any <APIProvider> mounts — the Maps JS SDK only reads
// `window.gm_authFailure` once when its loader script evaluates, so a
// late assignment misses the auth-failure event entirely.
installGoogleMapsAuthFailureHandler();

// Start Sentry as early as possible so it captures errors thrown during
// app bootstrap. No-op unless VITE_SENTRY_DSN is set (see lib/sentry.ts).
initSentry();

// "Never stale" strategy:
// - Global default is the 'fresh' tier (staleTime: 30s, not 0 — see @aiga/app-config/cache):
//   queries older than 30s refetch in the background. Users see cached data
//   instantly, then fresh data replaces it. ETags make these refetches cheap.
// - Lookup tables are overridden to 'static' (staleTime: Infinity) — they
//   never change at runtime, so they're fetched once per session.
// Every successful mutation appends an audit row on the backend (see
// emit-change-event.ts), which now awaits the insert before responding
// so the row is committed to the database by the time we get the
// success callback here. Invalidating the record-history root key on
// every mutation success makes any mounted history panel refetch
// immediately — no page reload required to see the new event.
//
// Scoping (B1b): a blanket `['record-history']` invalidate on EVERY
// mutation success — combined with a property page's large active-query
// set — fires a synchronized refetch burst right after a write. Against a
// single API instance that burst is what produced the "write succeeded
// (toast) but a follow-on read errored (banner), refresh fixes it"
// symptom. The invalidation is now driven by the mutation's `meta`:
//
//   - meta.skipRecordHistoryInvalidation === true  → skip entirely
//       (for non-audited mutations that never append an audit row).
//   - meta.recordHistory = { entity, id }          → invalidate only
//       that record's history key, not the whole tree.
//   - meta absent (the default)                    → keep the blanket
//       invalidate, so every generated CRUD mutation still refreshes any
//       mounted history panel live with no per-hook wiring.
//
// invalidateQueries only refetches ACTIVE queries, so this never spins up
// history requests for panels that aren't mounted.
//
// The property page's deals (usePropertyDeals) are one composed read of
// transactions + allocations under their own key, which no table mutation's
// own invalidation reaches — so every mutation success invalidates it too
// (one request, and only while a property page is mounted). The dashboard
// summary's composed parts (dashboardKeys: pinned properties, shared with me,
// the released-unviewed badge) likewise — one batched request. So is the
// /properties card view's hero read (one per page, keyed on the page's ids,
// which no per-property image invalidation reaches). And the /comps rows
// (compsRowsKeys), which carry each row's properties, parties, contacts and
// parcels — tables whose own keys never reach them. And a comp report's rows
// by id (compReportEntriesKeys: properties + sales + leases in one read).
// And the review queues' pages (reviewQueuesKeys), which carry user names,
// property labels, project titles and files.
const mutationCache = new MutationCache({
  onSuccess: (_data, _variables, _context, mutation) => {
    void queryClient.invalidateQueries({ queryKey: propertyDealsKeys.all });
    void queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    void queryClient.invalidateQueries({ queryKey: propertyFileKeys.heroesRoot });
    for (const queryKey of compsRowsKeys.roots) void queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({ queryKey: compReportEntriesKeys.all });
    for (const queryKey of reviewQueuesKeys.roots) void queryClient.invalidateQueries({ queryKey });
    const meta = mutation?.meta as
      | {
          skipRecordHistoryInvalidation?: boolean;
          recordHistory?: { entity: string; id: string };
        }
      | undefined;
    if (meta?.skipRecordHistoryInvalidation) return;
    if (meta?.recordHistory?.entity && meta.recordHistory.id) {
      queryClient.invalidateQueries({
        queryKey: ['record-history', meta.recordHistory.entity, meta.recordHistory.id],
      });
      return;
    }
    queryClient.invalidateQueries({ queryKey: ['record-history'] });
  },
});
const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: CACHE_TIERS.fresh.staleTime,
      gcTime: CACHE_TIERS.fresh.gcTime,
      refetchOnWindowFocus: CACHE_TIERS.fresh.refetchOnWindowFocus,
      refetchOnReconnect: CACHE_TIERS.fresh.refetchOnReconnect,
      retry: 1,
    },
  },
});

// Override lookup tables to cache forever (fetched once per session).
for (const entity of getStaticEntities()) {
  queryClient.setQueryDefaults([entity], getQueryDefaults(entity));
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* TODO: React Router v7 Migration - Add future flags to BrowserRouter:
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          See: https://reactrouter.com/v6/upgrading/future */}
      <BrowserRouter>
        <ColorSchemeProvider>
          <ToastProvider logoSrc="/colliers-logo.svg">
            <AuthProvider>
              <ErrorBoundary>
                <App />
              </ErrorBoundary>
            </AuthProvider>
            {/*
              react-hot-toast Toaster. The app has two toast systems in play:
              - ToastProvider above — branded success/error/warning/info (used
                by useToast())
              - Toaster here — react-hot-toast, used by utils/notifications.tsx
                (notify.success, notify.error, notify.confirm, etc.). Without
                this mount, notify.confirm's awaited promise never resolves —
                the confirm UI never renders — so callers like the Reset
                button on ManageUsersPage just silently hang.
            */}
            <Toaster position="bottom-right" />
          </ToastProvider>
        </ColorSchemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);